import React, { useState, useMemo, useEffect } from 'react';
import useF22Form from '../../../hooks/useF22Form';
import { getModule, hasModule } from './registry';
import '../../../styles/components/F22Form.css';
import { FaFilePdf, FaExternalLinkAlt, FaRedo } from 'react-icons/fa';
import { buildR17_R21_Html } from './2026/14d3/pdfExport';

/**
 * Página principal del formulario F-22 Declaración de Renta.
 * Carga dinámicamente el módulo correcto según año tributario y régimen.
 * Solo renderiza la sección activa (no las 14 a la vez).
 */
const F22FormPage = ({ anioTributario = 2026, regimen = '14d3' }) => {
  const {
    data,
    computed,
    loading,
    saving,
    dirty,
    isNew,
    pdfUrl,
    generatingPdf,
    updateField,
    updateNestedField,
    save,
    generatePdfFn,
  } = useF22Form(anioTributario, regimen);

  const [activeSection, setActiveSection] = useState('');

  const mod = useMemo(() => getModule(anioTributario, regimen), [anioTributario, regimen]);
  const SECTIONS = mod?.SECTIONS || [];

  useEffect(() => {
    if (SECTIONS.length > 0) {
      setActiveSection(SECTIONS[0].id);
    }
  }, [anioTributario, regimen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!hasModule(anioTributario, regimen) || SECTIONS.length === 0) {
    return (
      <div className="f22-loading">
        <p>Función en desarrollo</p>
      </div>
    );
  }

  // Solo obtener el componente de la sección activa (no las 14)
  const currentSection = SECTIONS.find((s) => s.id === activeSection);
  const ActiveComponent = currentSection ? mod[currentSection.component] : null;

  if (loading) {
    return (
      <div className="f22-loading">
        <div className="f22-spinner" />
        <p>Cargando declaración de renta...</p>
      </div>
    );
  }

  return (
    <div className="f22-page">
      {/* Header */}
      <div className="f22-header">
        <div className="f22-header-left">
          <h2 className="f22-title">
            Declaración de Renta AT {anioTributario}
          </h2>
          <span className="f22-regime-badge">
            {regimen === '14d3' ? 'Pro PYME (14D3)' : regimen.toUpperCase()}
          </span>
        </div>
        <div className="f22-header-right">
          {dirty && <span className="f22-unsaved-badge">Sin guardar</span>}
          {anioTributario === 2026 && regimen === '14d3' && (
            pdfUrl ? (
              <>
                <a
                  href={pdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="f22-btn-pdf f22-btn-pdf-view"
                  title="Ver PDF guardado"
                >
                  <FaExternalLinkAlt style={{ marginRight: 6, verticalAlign: 'middle' }} />
                  Ver PDF
                </a>
                <button
                  className="f22-btn-pdf-regen"
                  onClick={() => generatePdfFn(buildR17_R21_Html(data, computed))}
                  disabled={generatingPdf}
                  title="Regenerar PDF con datos actuales"
                >
                  <FaRedo style={{ marginRight: 6, verticalAlign: 'middle' }} />
                  {generatingPdf ? 'Generando...' : 'Regenerar'}
                </button>
              </>
            ) : (
              <button
                className="f22-btn-pdf"
                onClick={() => generatePdfFn(buildR17_R21_Html(data, computed))}
                disabled={generatingPdf}
                title="Generar y guardar PDF en la nube"
              >
                <FaFilePdf style={{ marginRight: 6, verticalAlign: 'middle' }} />
                {generatingPdf ? 'Generando...' : 'Generar PDF'}
              </button>
            )
          )}
          <button
            className="f22-btn-save"
            onClick={() => save()}
            disabled={saving}
          >
            {saving ? 'Guardando...' : isNew ? 'Guardar' : 'Actualizar'}
          </button>
        </div>
      </div>

      {/* Navegación por secciones */}
      <div className="f22-nav">
        {SECTIONS.map((section) => (
          <button
            key={section.id}
            className={`f22-nav-tab ${activeSection === section.id ? 'active' : ''}`}
            onClick={() => setActiveSection(section.id)}
          >
            {section.label}
          </button>
        ))}
      </div>

      {/* Solo renderiza la sección activa */}
      <div className="f22-content">
        {ActiveComponent && (
          <ActiveComponent
            data={data}
            computed={computed}
            updateField={updateField}
            updateNestedField={updateNestedField}
          />
        )}
      </div>
    </div>
  );
};

export default F22FormPage;
